/**
 * Formatting helpers: relative time (with raw ISO on hover), durations, and
 * domain vocabulary labels. No fabricated data - every function here is a
 * pure transform of a value the backend actually returned; callers are
 * responsible for handling null/missing values as empty/unavailable states.
 */

export const EMPTY = "-";

export function isValidDate(value: string | null | undefined): value is string {
  if (!value) return false;
  return !Number.isNaN(new Date(value).getTime());
}

/** "4m ago", "just now", "3h ago" - falls back to a short absolute date. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!isValidDate(iso)) return EMPTY;
  const date = new Date(iso);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export type AbsoluteTimeOptions = {
  /** IANA time zone. Defaults to India Standard Time for operator-facing UI. */
  timeZone?: string;
  /** Suffix shown after the clock time (e.g. "IST", "UTC"). */
  zoneLabel?: string;
};

/**
 * Absolute, operator-friendly timestamp parts for stacked table cells:
 *   Sep 17, 2026
 *   11:25 AM IST
 */
export function absoluteTimeParts(
  iso: string | null | undefined,
  options: AbsoluteTimeOptions = {}
): { date: string; time: string } | null {
  if (!isValidDate(iso)) return null;
  const timeZone = options.timeZone ?? "Asia/Kolkata";
  const zoneLabel =
    options.zoneLabel ?? (timeZone === "Asia/Kolkata" ? "IST" : timeZone === "UTC" ? "UTC" : "");
  const date = new Date(iso);
  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
  return {
    date: datePart,
    time: zoneLabel ? `${timePart} ${zoneLabel}` : timePart,
  };
}

/**
 * Single-line absolute timestamp for non-table surfaces (callouts, etc.).
 * Example: "Sep 15, 2026 • 4:01 PM IST"
 */
export function absoluteTime(
  iso: string | null | undefined,
  options: AbsoluteTimeOptions = {}
): string {
  const parts = absoluteTimeParts(iso, options);
  if (!parts) return EMPTY;
  return `${parts.date} • ${parts.time}`;
}

/** Duration between two ISO timestamps, e.g. "1m 12s", "2h 4m". Empty if either is missing. */
export function duration(startIso: string | null | undefined, endIso: string | null | undefined): string {
  if (!isValidDate(startIso) || !isValidDate(endIso)) return EMPTY;
  const startMs = new Date(startIso).getTime();
  const endMs = new Date(endIso).getTime();
  let diffSec = Math.round((endMs - startMs) / 1000);
  if (diffSec < 0) diffSec = 0;

  const hours = Math.floor(diffSec / 3600);
  const minutes = Math.floor((diffSec % 3600) / 60);
  const seconds = diffSec % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Sync entity -> display label. See §3 of frontend spec. */
export const ENTITY_ORDER = ["client", "resource", "project", "task", "allocation", "timesheet_entry"] as const;
export type SyncEntity = (typeof ENTITY_ORDER)[number];

const ENTITY_LABELS: Record<string, string> = {
  client: "Clients",
  resource: "Resources",
  project: "Projects",
  task: "Tasks",
  allocation: "Allocations",
  timesheet_entry: "Timesheets",
};

export function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity] ?? titleCaseWords(entity);
}

/** Entity A–Z (by label), then name A–Z within each entity. */
export function sortAccessGroups<T extends { entity: string; name: string }>(groups: readonly T[]): T[] {
  return [...groups].sort((a, b) => {
    const byEntity = entityLabel(a.entity).localeCompare(entityLabel(b.entity));
    if (byEntity !== 0) return byEntity;
    return a.name.localeCompare(b.name);
  });
}

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  service_account: "Service account",
  auth_group: "Access group",
  sync_settings: "Sync settings",
  admin_user: "Admin user",
  sync: "Sync",
};

export function resourceTypeLabel(resourceType: string | null | undefined): string {
  if (!resourceType) return EMPTY;
  return RESOURCE_TYPE_LABELS[resourceType] ?? titleCaseWords(resourceType);
}

/** Audit / mutation action codes → operator-facing labels. */
const ACTION_LABELS: Record<string, string> = {
  "service.create": "Service created",
  "service.update": "Service updated",
  "service.key_refresh": "API key refreshed",
  "service.key_revoke": "API key revoked",
  "service.disable": "Service disabled",
  "group.create": "Access group created",
  "group.update": "Access group updated",
  "group.delete": "Access group deleted",
  "sync_settings.update": "Sync settings updated",
  "auth.login": "Signed in",
  "auth.logout": "Signed out",
  "access_request.create": "Access requested",
  "access_request.approve": "Access approved",
  "access_request.reject": "Access rejected",
  "sync.trigger": "Sync started",
};

export function actionLabel(action: string | null | undefined): string {
  if (!action) return EMPTY;
  return ACTION_LABELS[action] ?? titleCaseWords(action);
}

const ACTOR_TYPE_LABELS: Record<string, string> = {
  entra_user: "Microsoft user",
  admin_key: "Admin key",
};

export function actorTypeLabel(actorType: string | null | undefined): string {
  if (!actorType) return EMPTY;
  return ACTOR_TYPE_LABELS[actorType] ?? titleCaseWords(actorType);
}

const ADMIN_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

export function adminStatusLabel(status: string | null | undefined): string {
  if (!status) return EMPTY;
  return ADMIN_STATUS_LABELS[status] ?? titleCaseWords(status);
}

/** Title-case words split on `.`, `_`, `-`, or whitespace. */
export function titleCaseWords(value: string): string {
  return value
    .replace(/[._-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return EMPTY;
  return value.toLocaleString();
}
